import React, { useState } from 'react';
import { Button } from '../ui/button';
import { 
  ArrowRight, 
  ArrowLeft,
  Briefcase,
  Trophy,
  Target,
  TrendingUp,
  Sparkles,
  FileText,
  CheckCircle
} from 'lucide-react';

/**
 * RV.1: 3-Step Guided Input Wizard
 * Collects user input for guest review generation with minimal friction.
 * Designed to be completed in ~90 seconds.
 */

const STEPS = [
  {
    id: 'role',
    title: 'About Your Role',
    subtitle: 'Help us understand your position',
    icon: Briefcase
  },
  {
    id: 'achievements',
    title: 'Your Key Wins',
    subtitle: 'What are you proud of this quarter?',
    icon: Trophy
  },
  {
    id: 'growth',
    title: 'Growth & Goals',
    subtitle: 'Almost there!',
    icon: TrendingUp
  }
];

const GuidedInput = ({ onComplete, onCancel, accentColor = '#dc2626' }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    role: '',
    context: '',
    achievements: ['', '', ''],
    challenges: '',
    growth: '',
    goals: '',
    optional_paste: ''
  });
  const [showOptionalPaste, setShowOptionalPaste] = useState(false);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateAchievement = (index, value) => {
    setFormData(prev => {
      const newAchievements = [...prev.achievements];
      newAchievements[index] = value;
      return { ...prev, achievements: newAchievements };
    });
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return formData.role.trim().length >= 2 && formData.context.trim().length >= 10;
      case 1:
        return formData.achievements.filter(a => a.trim().length > 0).length >= 1;
      case 2:
        return true; // Optional fields
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    // Filter out empty achievements
    const cleanedData = {
      ...formData,
      achievements: formData.achievements.filter(a => a.trim().length > 0),
      flow_type: 'review'
    };
    
    // Store in sessionStorage for recovery after signup
    sessionStorage.setItem('rv_input', JSON.stringify({
      ...cleanedData,
      created_at: new Date().toISOString()
    }));
    
    onComplete(cleanedData);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                What's your role? *
              </label>
              <input
                type="text"
                value={formData.role}
                onChange={(e) => updateField('role', e.target.value)}
                placeholder="e.g., Senior Product Manager, Engineering Lead, Sales Director"
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500/50"
                data-testid="rv-input-role"
                autoFocus
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Brief context about your responsibilities *
              </label>
              <textarea
                value={formData.context}
                onChange={(e) => updateField('context', e.target.value)}
                placeholder="e.g., Leading a team of 5 engineers building the mobile app. Responsible for roadmap, stakeholder alignment, and delivery..."
                rows={3}
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500/50 resize-none"
                data-testid="rv-input-context"
              />
              <p className="text-xs text-slate-500 mt-1">
                {formData.context.length}/500 characters
              </p>
            </div>
          </div>
        );
      
      case 1:
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 mb-4">
              List 1-3 achievements or wins from this period. Even small things count!
            </p>
            
            {formData.achievements.map((achievement, index) => (
              <div key={index} className="relative">
                <div className="absolute left-4 top-3.5 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-medium text-slate-500">
                  {index + 1}
                </div>
                <input
                  type="text"
                  value={achievement}
                  onChange={(e) => updateAchievement(index, e.target.value)}
                  placeholder={
                    index === 0 ? "e.g., Launched v2.0 of the mobile app ahead of schedule" :
                    index === 1 ? "e.g., Improved team velocity by 30% through process changes" :
                    "e.g., Successfully onboarded 3 new team members"
                  }
                  className="w-full pl-14 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500/50"
                  data-testid={`rv-input-achievement-${index}`}
                  autoFocus={index === 0}
                />
              </div>
            ))}
            
            <div className="pt-4 border-t border-slate-100">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Any challenges you overcame? (optional)
              </label>
              <input
                type="text"
                value={formData.challenges}
                onChange={(e) => updateField('challenges', e.target.value)}
                placeholder="e.g., Navigated team restructuring while maintaining delivery"
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500/50"
                data-testid="rv-input-challenges"
              />
            </div>
          </div>
        );
      
      case 2:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Something you learned or improved? (optional)
              </label>
              <input
                type="text"
                value={formData.growth}
                onChange={(e) => updateField('growth', e.target.value)}
                placeholder="e.g., Became more comfortable with difficult conversations"
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500/50"
                data-testid="rv-input-growth"
                autoFocus
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                A goal you're working toward? (optional)
              </label>
              <input
                type="text"
                value={formData.goals}
                onChange={(e) => updateField('goals', e.target.value)}
                placeholder="e.g., Preparing for a leadership role, learning new technology"
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500/50"
                data-testid="rv-input-goals"
              />
            </div>
            
            {/* Optional paste section */}
            <div className="pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowOptionalPaste(!showOptionalPaste)}
                className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
              >
                <FileText className="w-4 h-4" />
                <span>{showOptionalPaste ? 'Hide' : 'Have notes or feedback to paste?'}</span>
              </button>
              
              {showOptionalPaste && (
                <div className="mt-3">
                  <textarea
                    value={formData.optional_paste}
                    onChange={(e) => updateField('optional_paste', e.target.value)}
                    placeholder="Paste any relevant content: Slack messages, feedback from your manager, notes, previous reviews, CV sections..."
                    rows={4}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500/50 resize-none text-sm"
                    data-testid="rv-input-optional-paste"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    This helps us write a more personalized review
                  </p>
                </div>
              )}
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-xl w-full" data-testid="rv-guided-input">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {React.createElement(STEPS[currentStep].icon, { 
              className: "w-5 h-5",
              style: { color: accentColor }
            })}
            <div>
              <p className="font-semibold text-slate-900">{STEPS[currentStep].title}</p>
              <p className="text-sm text-slate-500">{STEPS[currentStep].subtitle}</p>
            </div>
          </div>
          {onCancel && (
            <button
              onClick={onCancel}
              className="text-slate-400 hover:text-slate-600 text-sm"
            >
              Cancel
            </button>
          )}
        </div>
        
        {/* Progress bar */}
        <div className="flex gap-2">
          {STEPS.map((step, index) => (
            <div
              key={step.id}
              className="flex-1 h-1.5 rounded-full overflow-hidden bg-slate-200"
            >
              <div
                className="h-full transition-all duration-300"
                style={{
                  width: index < currentStep ? '100%' : index === currentStep ? '50%' : '0%',
                  backgroundColor: accentColor
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Form content */}
      <div className="p-6">
        {renderStep()}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={handleBack}
          disabled={currentStep === 0}
          className="text-slate-600"
          data-testid="rv-btn-back"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500">
            Step {currentStep + 1} of {STEPS.length}
          </span>
          
          <Button
            onClick={handleNext}
            disabled={!canProceed()}
            className="text-white"
            style={{ backgroundColor: canProceed() ? accentColor : undefined }}
            data-testid="rv-btn-next"
          >
            {currentStep === STEPS.length - 1 ? (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Generate Review
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GuidedInput;
